import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { filterProps, formatPrice, isInventoryStatusLoaded } from 'helpers/utils'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Theme from 'components/common/Theme/Theme'
import PromoTeaser from 'components/common/PromoTeaser'
import Link, { A } from 'components/common/Link/Link'
import PhoneLink from 'components/common/PhoneLink'
import CompatibilityLabel from 'components/common/CompatibilityLabel'
import SpecialOrder from 'components/common/SpecialOrder'
import Button from 'components/common/Button/Button'
import Icon from 'components/common/Icon/Icon'
import TireAvailability from 'components/common/TireAvailability'

import Overlay from '../Overlay'
import * as UI from '../../../Common.styles'
import ProductQuantityInput from '../ProductQuantityInput'
import OTDPAddons from '../OTDPAddons'
import RemoveItemButton from '../RemoveItemButton'
import CartItem from '../CartItem'
import {
  STAGGERED_SIZE_LABEL,
  SAVE_FOR_LATER_BTN_TEXT,
  ITEM_TEXT,
  SAVED_FOR_LATER_TEXT,
  SCROLL_TEXT
} from './constants'
import {
  CALL_STORE_FOR_PRICE_FIRST_TEXT,
  NOT_AVAILIABLE_FIRST_TEXT,
  NOT_AVAILIABLE_SECOND_TEXT
} from 'constant'
import useTireAvailabilityStatus from 'hooks/useTireAvailabilityStatus.js'
import { AvailabilityMobileWrapper, AvailabilityWrapper } from './styles'
import UpdatingBlock from 'components/common/UpdatingBlock'

const Product = ({
  product: {
    otdpAddons,
    brandLineName,
    tireType,
    productName,
    brandImageUrl,
    skuImageUrl,
    listPrice,
    totalPrice,
    rawTotalPrice,
    isActive,
    available,
    qty = '',
    description,
    commerceItemId,
    compatibilityState,
    isSpecialOrder,
    tireDetailsUrl,
    bvId,
    skuId
  },
  product,
  productPromotions = [],
  instantSavingsPromotions = [],
  isStaggeredSku,
  className,
  myStore,
  storePhoneNumber = '',
  formattedStorePhoneNumber = '',
  showStoreLocatorModalInAppointmentMode,
  showOtdpMessage,
  otdpMessage: [otdpMessageFirst = '', otdpMessageLast = ''] = [],
  inActiveSkuMessage,
  expanded,
  enableEdit,
  toggleHandlers,
  vehicleName,
  index,
  handleTAOSDateTimeUpdate,
  handleSaveForLater,
  isSavedForLater,
  handleProductClick,
  setAvailability,
  setSkusAddedToCart,
  inventoryStatuses,
  isPilotStore,
  activeVehicleId,
  ...props
}) => {
  /* this custom hook is designed to make inventory service call when Cart page loads
  and when quantity is updated */
  const [availability] = useTireAvailabilityStatus({
    countFront: qty,
    brandName: brandLineName,
    frontTireBvId: bvId,
    storeNumber: myStore?.storeNumber,
    productSkuId: skuId,
    dependencies: [qty, isPilotStore],
    isPilotStore,
    contextType: 'quote',
    activeVehicleId
  }) || []

  const renderTireAvailability = (Wrapper) => (
    <Wrapper>
      <TireAvailability lg availability={ availability } />
    </Wrapper>
  )

  return (
    <ItemContainer id={ `product-container${commerceItemId}` }>
      <UpdatingBlock condition={ isInventoryStatusLoaded(inventoryStatuses, product.skuId) }>
        <BackgroundNote className="background-block">
          <div>
            <CheckCircleIcon
              alt=""
              asset="check-circle"
              float="left" />
            <BackgroundNoteText>{`${ITEM_TEXT} `}</BackgroundNoteText>
            <BackgroundNoteText highlighted>{SAVED_FOR_LATER_TEXT}</BackgroundNoteText>
          </div>
          <CTAWrapper>
            <ScrollDownIcon alt="Scroll down" asset="scroll-down" />
            <CTAText>{SCROLL_TEXT}</CTAText>
          </CTAWrapper>
        </BackgroundNote>
        <UI.ItemWrapper className={ className } isSavedForLater={ isSavedForLater } { ...props }>
          <Media.Mobile>
            {renderTireAvailability(AvailabilityMobileWrapper)}
          </Media.Mobile>
          <CartItem>
            <Cell disabled={ !isActive }>
              <Media.Desktop>
                <Link to={ tireDetailsUrl } onClick={ handleProductClick }>
                  <Image alt={ productName } data-at-quote-product-image src={ `${skuImageUrl}?$BOT-Cart-Tire-Desktop-w144$` } />
                </Link>
              </Media.Desktop>
              <Media.Mobile>
                <Link to={ tireDetailsUrl } onClick={ handleProductClick }>
                  <Image alt={ productName } data-at-quote-product-image src={ `${skuImageUrl}?$BOT-Cart-Tire-Mobile-w60$` } />
                </Link>
              </Media.Mobile>
              <Info>
                <Media.Desktop>
                  {renderTireAvailability(AvailabilityWrapper)}
                </Media.Desktop>
                <Link to={ tireDetailsUrl } onClick={ handleProductClick }>
                  <Logo alt={ brandLineName } data-at-quote-product-logo src={ brandImageUrl } />
                  <TireName data-at-quote-product-name>{ productName }</TireName>
                </Link>
                { isStaggeredSku && <TireSizeLabel>{STAGGERED_SIZE_LABEL[tireType]}{' '}</TireSizeLabel> }
                <Size data-at-quote-product-size>{ description }</Size>
                <InfoSubWrapper>
                  <CompatibilityLabel
                    status={ compatibilityState }
                    vehicle={ vehicleName }
                    dense={ isSpecialOrder } />
                  { isSpecialOrder && <SpecialOrder /> }
                  {
                    productPromotions.map(({ promotionName, promotionId, promotionType, promotionRebate }, index) =>
                      <PromoTeaser
                        creative="tire-shopping-cart-quote"
                        index={ index }
                        item={ productPromotions[index] }
                        key={ index }
                        disabled={ !isActive }
                        promotionId={ promotionId }
                        promotionRebate={ promotionRebate }
                        promotionType={ promotionType }>
                        { promotionName }
                      </PromoTeaser>
                    )
                  }
                </InfoSubWrapper>
              </Info>
              <SaveForLaterBtnWrapper>
                <SaveForLaterBtn secondary narrow saveForLater onClick={ handleSaveForLater }>
                  {SAVE_FOR_LATER_BTN_TEXT}
                </SaveForLaterBtn>
              </SaveForLaterBtnWrapper>
            </Cell>
            <Cell quantity left>
              { enableEdit ?
                <Fragment>
                  <QuantityFieldset disabled={ !isActive }>
                    <QuantityLegend>Quantity</QuantityLegend>
                    <QuantityInput
                      value={ qty }
                      disabled={ !isActive }
                      inputId={ `Product-Quantity-Control-${index}` }
                      commerceItemId={ commerceItemId } />
                  </QuantityFieldset>
                  <RemoveButton product={ product } commerceItemId={ commerceItemId } />
                </Fragment> :
                <Fragment>
                  <UI.Mobile>Quantity</UI.Mobile>
                  <span>{ qty }</span>
                </Fragment>
              }
            </Cell>
            { isActive ?
              <Fragment>
                <Cell unitPrice left>
                  <UI.Mobile>Unit Price</UI.Mobile>
                  {
                  // when store is not selected BE send price: -1.
                  // And we should check, if price < 0 we need to show "Not Available" title
                    listPrice > 0 ?
                      <Price data-at-quote-product-price enableEdit={ enableEdit }>{ formatPrice(listPrice) }</Price> :
                      <Available>Not Available</Available>
                  }
                </Cell>
                <Cell totalPrice right>
                  {
                  // when store is not selected BE send totalPrice: -1.
                  // And we should check, if totalPrice < 0 we need to show "find store for price" link
                    totalPrice > 0 ?
                      <Fragment>
                        <UI.Mobile>Total Price</UI.Mobile>
                        <Price data-at-quote-total-product-price total enableEdit={ enableEdit }>
                          { formatPrice(rawTotalPrice) }
                        </Price>
                        {
                          (instantSavingsPromotions || []).map(({ promotionName, adjustmentAmount }) =>
                            <InstantSavingsPromotion key={ `${promotionName}-${adjustmentAmount}` }>
                              { promotionName }
                              <InstantSavingsAdjustment>
                                -${ Math.abs(adjustmentAmount).toFixed(2) }
                              </InstantSavingsAdjustment>
                            </InstantSavingsPromotion>
                          )
                        }
                      </Fragment> :
                      <Fragment>
                        <UI.Mobile>Total Price</UI.Mobile>
                        {
                          myStore && totalPrice < 0 ?
                            <Fragment>
                              <CallStore>
                                { CALL_STORE_FOR_PRICE_FIRST_TEXT }
                                <Media.Mobile>
                                  <StyledPhoneLink number={ storePhoneNumber } />
                                </Media.Mobile>
                                <Media.Desktop>
                                  <Phone>{ formattedStorePhoneNumber }</Phone>
                                </Media.Desktop>
                              </CallStore>
                            </Fragment> :
                            <StoreLink prevent to="/" bolder underline handler={ () => showStoreLocatorModalInAppointmentMode(handleTAOSDateTimeUpdate) }>
                              Find Store for Price
                            </StoreLink>
                        }
                      </Fragment>
                  }
                </Cell>
              </Fragment> :
              <Cell definedWidth right long>
                <Discontinued>{ inActiveSkuMessage }</Discontinued>
              </Cell>
            }
          </CartItem>
          {
            otdpAddons && isActive &&
            <OTDPAddonsWrapper>
              <OTDPAddons
                otdpAddons={ otdpAddons }
                commerceItemId={ commerceItemId }
                quantity={ qty }
                enableEdit={ enableEdit } />
            </OTDPAddonsWrapper>
          }
          {
            showOtdpMessage && isActive &&
            <UI.OtdPricingMessage>
              <UI.StyledInfoIcon asset="icon-info" alt="" />
              <OtdpMessageBlock>
                { otdpMessageFirst }
                <Media.Mobile component="span">
                  <StyledPhoneLink number={ storePhoneNumber } inline />
                </Media.Mobile>
                <Media.Desktop component="span">
                  { formattedStorePhoneNumber }
                </Media.Desktop>
                { otdpMessageLast }
              </OtdpMessageBlock>
            </UI.OtdPricingMessage>
          }
          {
            !available &&
            <Overlay>
              <UI.Content>
                { NOT_AVAILIABLE_FIRST_TEXT }
                <Media.Mobile>
                  <PhoneLink number={ storePhoneNumber } />
                </Media.Mobile>
                <Desktop>
                  { formattedStorePhoneNumber }
                </Desktop>
                { NOT_AVAILIABLE_SECOND_TEXT }
              </UI.Content>
              <A prevent bolder underline to="/" handler={ toggleHandlers.onClick }>Continue</A>
            </Overlay>
          }
        </UI.ItemWrapper>
      </UpdatingBlock>
    </ItemContainer>
  )
}

Product.propTypes = {
  activeVehicleId: PropTypes.string,
  className: PropTypes.string,
  compatibilityState: PropTypes.string,
  enableEdit: PropTypes.bool,
  expanded: PropTypes.bool,
  formattedStorePhoneNumber: PropTypes.string,
  handleProductClick: PropTypes.func,
  handleSaveForLater: PropTypes.func,
  handleTAOSDateTimeUpdate: PropTypes.func,
  inActiveSkuMessage: PropTypes.string,
  index: PropTypes.number,
  instantSavingsPromotions: PropTypes.arrayOf(PropTypes.shape()),
  inventoryStatuses: PropTypes.arrayOf(PropTypes.shape()),
  isPilotStore: PropTypes.bool,
  isSavedForLater: PropTypes.bool,
  isStaggeredSku: PropTypes.bool,
  myStore: PropTypes.shape(),
  otdpMessage: PropTypes.arrayOf(PropTypes.string),
  product: PropTypes.shape({
    otdpAddons: PropTypes.arrayOf(PropTypes.shape()),
    brandLineName: PropTypes.string,
    tireType: PropTypes.string,
    productName: PropTypes.string,
    brandImageUrl: PropTypes.string,
    bvId: PropTypes.string,
    skuImageUrl: PropTypes.string,
    skuId: PropTypes.string,
    tireDetailsUrl: PropTypes.string,
    price: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    listPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    unitPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    totalPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    rawTotalPrice: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    isActive: PropTypes.bool,
    tireSize: PropTypes.shape(),
    available: PropTypes.bool,
    qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    commerceItemId: PropTypes.string,
    compatibilityState: PropTypes.string,
    isSpecialOrder: PropTypes.bool
  }),
  productPromotions: PropTypes.arrayOf(PropTypes.shape()),
  setAvailability: PropTypes.func,
  setSkusAddedToCart: PropTypes.func,
  showOtdpMessage: PropTypes.bool,
  showStoreLocatorModalInAppointmentMode: PropTypes.func,
  storePhoneNumber: PropTypes.string,
  toggleHandlers: PropTypes.shape({
    onClick: PropTypes.func
  }),
  vehicleName: PropTypes.string
}

export default Product

const Desktop = styled.span`
  padding-left: 10px;
  ${Mixin.font.GothamBold()}
  line-height: 22px;
  ${Mixin.desktopOnly()};
`

const propsToRemove = [
  'definedWidth',
  'quantity',
  'unitPrice',
  'totalPrice',
  'long',
  'right',
  'left'
]

const ItemContainer = styled.div`
  position: relative;
`
const BackgroundNote = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const BackgroundNoteText = styled.span`
  white-space: pre-wrap;
  ${props => props.highlighted && css`
    color: ${Theme.colors.green};
    ${Mixin.font.GothamBold()}
  `}
`

const CTAWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 10px 0 0 0;
`

const ScrollDownIcon = styled(Icon)`
  width: 30px;
  margin: 0 0 5px;
`

const CTAText = styled.span`
  text-transform: uppercase;
  font-size: 10px;
`

const CheckCircleIcon = styled(Icon)`
  height: 15px;
  margin: -2px 5px 0 0;
`

const Cell = styled(props => <div { ...filterProps(props, propsToRemove) } />)`
  vertical-align: top;
  ${Media.desktop`
    display: table-cell;
  `}
  ${props => props.definedWidth && css`
    ${Media.desktop`
      width: 165px;
    `}
  `}
  ${props => props.quantity && css`
    ${Media.desktop`
      width: 150px;
    `}
    ${Media.tablet`
      width: 110px;
    `}
  `}
  ${props => props.unitPrice && css`
    ${Media.desktop`
      width: 120px;
    `}
    ${Media.tablet`
      width: 95px;
    `}
  `}
  ${props => props.totalPrice && css`
    ${Media.desktop`
      width: 120px;
    `}
  `}
  ${Media.desktop`
    ${props => props.right && css`
      text-align: right;
    `}
  `}
  ${Media.desktop`
    ${props => props.left && css`
      text-align: left;
    `}
  `}
  ${Mixin.responsive('padding', '30px 3px', '0 0 10px')}
  ${Mixin.clearFix()}
  ${props => props.disabled && css`
    opacity: 0.5;
  `}

  &:last-child {
    ${Media.desktop`
      width: ${props => props.long ? '315px' : '150px'};
      padding-right: 20px;
    `}
    ${Media.mobile`
      padding: 0;
    `}
  }
`

const SaveForLaterBtnWrapper = styled.div`
  ${Mixin.responsive('margin-left', '10px', '5px')}
  ${Mixin.responsive('margin-top', '10px', '10px')}
  ${Media.mobile`
    position: absolute;
    top: 95px;
    left: 0px;
    transform: scale(0.8);
    margin-left: 0;
  `}
`

const SaveForLaterBtn = styled(Button)`
  padding: 4px 5px;
  ${Media.mobile`
    font-size: 12px;
    white-space: nowrap;
  `}
`

const Image = styled.img`
  position: relative;
  z-index: 1;
  display: inline-block;
  vertical-align: top;
  ${Media.desktop`
    max-height: 144px;
  `}
  ${Mixin.responsive('max-width', '144px', '60px')}
  ${Media.mobile`
    position: relative;
    left: 20px;
  `}
`

const Info = styled.div`
  display: inline-block;
  width: 100%;
  ${Mixin.responsive('padding-left', '150px', '125px')}
  ${Mixin.responsive('margin-left', '-150px', '-65px')}
  ${Mixin.responsive('vertical-align', 'middle', 'top')}
  ${Media.mobile`
    min-height: 120px;
  `}
`


const InfoSubWrapper = styled.div`
  ${Media.desktop`
    margin-right: -320px;
  `}
`

const TireSizeLabel = styled.span`
  ${Mixin.font.GothamBold(14)}
`

const Size = styled.div`
  display: inline-block;
  margin-bottom: 15px;
  line-height: 22px;
  text-transform: uppercase;
`

const TireName = styled.div`
  ${Mixin.font.GothamMedium(18, 500, 24)}
`

const Logo = styled.img`
  max-width: 110px;
`

const Price = styled.div`
  line-height: 22px;
  color: ${Theme.colors.sub_grey};
  ${Media.mobile`
    display: inline-block;
  `}
  ${props => props.old && css`
    color: ${Theme.colors.caption_grey};
    text-decoration: line-through;
    ${Media.mobile`
      margin-left: 10px;
    `}
  `}
  ${props => props.total && css`
    font-weight: 600;
  `}

  &:first-of-type {
    ${props => props.enableEdit && css`
      ${Media.desktop`
        margin-top: 30px;
      `}
    `}
  }
`

const CallStore = styled.div`
  ${Mixin.font.GothamBold()}
  line-height: 22px;
  ${Media.desktop`
    margin-top: 30px;
  `}
  ${Media.mobile`
    display: inline-block;
    vertical-align: top;
  `}
`

const Phone = styled.div`
  ${Mixin.font.GothamBold()}
  line-height: 22px;
`

const StyledPhoneLink = styled(PhoneLink)`
  display: block;
  ${Mixin.font.GothamBold()}
  line-height: 22px;
  ${props => props.inline && css`
    display: inline;
  `}
`

const StoreLink = styled(A)`
  display: inline-block;
  ${Mixin.responsive('line-height', '22px', '19px')}
  ${Mixin.responsive('margin-top', '30px', '5px')}
`

const Available = styled.span`
  display: inline-block;
  line-height: 22px;
  color: ${Theme.colors.caption_grey};
  ${Mixin.responsive('margin-top', '30px', '5px')}
`

const Discontinued = styled(Available)`
  font-weight: bold;
  color: ${Theme.colors.red};
`

const OTDPAddonsWrapper = styled.div`
  ${Mixin.responsive('padding', '0px 20px', '0px 10px')}
`

const InstantSavingsPromotion = styled.div`
  margin-top: 10px;
  ${Mixin.font.GothamBold()}
  color: ${Theme.colors.green};
  ${Media.desktop`
    margin-left: -700px;
  `}
`

const InstantSavingsAdjustment = styled.span`
  font-weight: bold;
  margin-left: 30px;
`

const QuantityFieldset = styled.fieldset`
  display: inline-block;
  ${props => props.disabled && css`
    opacity: 0.5;
  `}
`

const QuantityLegend = styled(UI.Mobile.withComponent('legend'))`
  line-height: 30px;
  float: left;
`

const OtdpMessageBlock = styled.div`
  display: inline-block;

  span {
    font-weight: bold;
  }
`

const QuantityInput = styled(ProductQuantityInput)`
  ${Media.mobile`
    width: 100px;
  `}
`

const RemoveButton = styled(RemoveItemButton)`
  ${Media.mobile`
    margin-top: 7px;
  `}
`
